import React, { useState } from "react"
import classNames from "classnames"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { useDispatch, useSelector } from "react-redux"

import { Checkbox, Modal, Spinner, LabelPill } from "../../../common"
import { deleteCvs } from "../../../../services/cv"
import { setCvs } from "../../../../redux/slices/cvSlice"
import { statuses } from "../SelectCVPage"
import { setSelectedCVs } from "../../../../redux/slices/jobSlice"
import { FormattedMessage, useIntl } from "react-intl"

const CVItem = ({
  cv,
  selected,
  onChange,
  isFile,
  isUploading,
  viewLink,
  readOnly = false,
  labels = [],
  maxAllowedLabels = 4,
  handleLabelsIconClick = (cvId = null) => {},
}) => {
  const selectedCVs = useSelector((state) => state.jobs.selectedCVs)

  const [hovered, setHovered] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const cvs = useSelector((state) => state.cv.cvs)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)

  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const navigate = useNavigate()

  const handleDeleteConfirm = async () => {
    try {
      const id = showDeleteConfirmModal

      if (!id) return

      setDeleteLoading(true)

      await deleteCvs([id])
      const thisCv = cvs.find((cv) => cv.id === id)
      toast.success(
        formatMessage(
          { id: "toast.cvDeleted" },
          {
            fileName: `${thisCv.information.first_name ?? ""} ${
              thisCv.information.last_name ?? ""
            }`.trim(),
          },
        ),
      )

      dispatch(setSelectedCVs(selectedCVs.filter((cv) => cv !== id)))
      dispatch(setCvs(cvs.filter((cv) => cv.id !== id && cv.originalId !== id)))
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          formatMessage({ id: "toast.failedToDeleteCV" }),
      )
    }
    setShowDeleteConfirmModal(false)
    setDeleteLoading(false)
  }

  const handleCVClick = () => {
    navigate(viewLink, { state: { cv } })
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirmModal(cv.id)
  }

  // `cv.source` comes in two shapes today:
  //   1) the legacy ATS-import object: { greenhouse: true } / { lever: true }
  //   2) the lightweight string set by sourcing pending CVs: "sourcing"
  // Treat the string form separately so we get a green "Sourced" pill on
  // sourced rows while they're still parsing.
  const isSourced = cv.source === "sourcing"
  const source = !isSourced && (cv.source?.greenhouse
    ? formatMessage({ id: "common.importedFromGreenhouse" })
    : cv.source?.lever
    ? formatMessage({ id: "common.importedFromLever" })
    : null)

  return (
    <>
      <div
        onMouseLeave={() => setHovered(false)}
        onMouseEnter={() => setHovered(true)}
        className={classNames("cv-item", {
          "cv-item--uploading": isUploading,
          "cv-item--pending": cv.status !== "COMPLETED",
          "cv-item--with-source": source,
          "cv-item--readonly": readOnly,
          "cv-item--duplicated": cv.isDuplicated,
        })}
        onClick={(e) => {
          if (
            ["P", "DIV", "SPAN"].includes(e.target.tagName) &&
            cv.status === "COMPLETED" &&
            !cv.isDuplicated
          )
            onChange({ id: cv.id, checked: !selected })
        }}
      >
        {(hovered || selected) &&
          cv.status === "COMPLETED" &&
          !cv.isDuplicated &&
          !readOnly && (
            <Checkbox
              variant="secondary"
              value={selected}
              onChange={(e) => onChange({ id: cv.id, checked: e.target.checked })}
            />
        )}

        {isUploading && (
          <div className="cv-item__info">
            <p className="cv-item__name">
              {cv.file_name}
            </p>
          </div>
        )}

        {cv?.information && (
          <div className="cv-item__info">
            <p
              className={classNames("cv-item__name", {
                "cv-item__name--selected": selected,
                "cv-item__name--duplicated": cv.isDuplicated,
              })}
            >
              {cv.information.file_name
                ? cv.information.file_name
                : `${cv.information.first_name || "N/A"} ${
                    cv.information.last_name
                  }`}

              <br />

              {isSourced && (
                <span className="just-sourced-tag">
                  <FormattedMessage id="sourcing.justSourced.tag" />
                </span>
              )}

              {cv.experience?.length > 0 && (
                <span>
                  {cv.experience.at(0)?.title}
                </span>
              )}
            </p>

            {labels && labels.length > 0 && (
              <div className="cv-item__labels">
                {labels.slice(0, maxAllowedLabels).map((label) => (
                  <LabelPill key={label.id} label={label} size="small" />
                ))}
                {labels.length > maxAllowedLabels && (
                  <span className="label-pill label-pill--small" style={{ backgroundColor: '#ccc' }}>
                    +{labels.length - maxAllowedLabels}
                  </span>
                )}
              </div>
            )}

            {source && <span className="cv-item__source">{source}</span>}

            {cv.isDuplicated && (
              <p className="cv-item__duplicated">
                <FormattedMessage id="common.duplicatedCV" />
              </p>
            )}
          </div>
        )}

        <div className="cv-item__actions">
          {hovered && cv.status === "COMPLETED" && (
            <div className="cv-item__link" onClick={handleCVClick}>
              <FormattedMessage id={`common.${cv.isDuplicated ? "viewOriginal" : "view"}`} />
            </div>
          )}

          {isFile && cv.status === statuses.FAILED ? (
            <p
              className={classNames({
                "cv-item__status--failed": cv.status === statuses.FAILED,
              })}
            >
              {cv.status}
            </p>
          ) : isUploading ? (
            <p className="cv-item__status">
              <FormattedMessage id="common.uploading" />
            </p>
          ) : cv.status !== "COMPLETED" ? (
            <p className="cv-item__status">
              <FormattedMessage id="common.processing" />
            </p>
          ) : (
            (!hovered || (readOnly && hovered)) && (
              <p
                className={classNames("cv-item__status", {
                  "cv-item__status--selected": selected,
                })}
              >
                {/* {fromNow(cv.created_at, teamLanguage)} */}
                {cv.created_at
                  ? new Date(cv.created_at).toLocaleDateString()
                  : "N/A"
                }
              </p>
            )
          )}

          {!isFile && !readOnly && hovered && (
            <>
              <button
                onClick={() => handleLabelsIconClick(cv.id)}
                className="btn btn--small btn--icon labels-icon"
              >
                {labelsIcon}
              </button>
              <button
                onClick={handleDeleteClick}
                className="btn btn--small btn--icon delete-icon"
              >
                {deleteIcon}
              </button>
            </>
          )}
        </div>
      </div>

      {/* // TODO: Move this to a separate component that should be outside the list */}
      <Modal
        open={Boolean(showDeleteConfirmModal)}
        setOpen={setShowDeleteConfirmModal}
        className={"delete-dialog"}
      >
        <div className="delete-dialog__container">
          <h2 className="text-2xl font-bold">
            <FormattedMessage id="common.confirmDelete" />
          </h2>
          <p>
            <FormattedMessage id="common.areYouSure" />
          </p>
          {deleteLoading && <Spinner />}
          <div className="flex gap-2 delete-dialog__buttons">
            <button
              className={classNames("btn btn--small primary mr-2", {
                disabled: deleteLoading,
              })}
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              <FormattedMessage id="common.yes" />
            </button>
            <button
              className="btn btn--small"
              onClick={() => setShowDeleteConfirmModal(false)}
            >
              <FormattedMessage id="common.no" />
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

const deleteIcon = (
  <svg
    width="14"
    height="18"
    viewBox="0 0 14 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0.106934 3.6873H1.56533H13.2325" fill="white" />
    <path
      d="M11.7741 3.6873V13.8961C11.7741 14.2829 11.6205 14.6538 11.347 14.9273C11.0735 15.2008 10.7025 15.3545 10.3157 15.3545H3.02373C2.63694 15.3545 2.26599 15.2008 1.99249 14.9273C1.71898 14.6538 1.56533 14.2829 1.56533 13.8961V3.6873M3.75293 3.6873V2.22891C3.75293 1.84211 3.90658 1.47117 4.18008 1.19766C4.45359 0.92416 4.82454 0.770508 5.21133 0.770508H8.12812C8.51492 0.770508 8.88587 0.92416 9.15937 1.19766C9.43287 1.47117 9.58652 1.84211 9.58652 2.22891V3.6873"
      fill="white"
    />
    <path
      d="M0.106934 3.6873H1.56533M1.56533 3.6873H13.2325M1.56533 3.6873V13.8961C1.56533 14.2829 1.71898 14.6538 1.99249 14.9273C2.26599 15.2008 2.63694 15.3545 3.02373 15.3545H10.3157C10.7025 15.3545 11.0735 15.2008 11.347 14.9273C11.6205 14.6538 11.7741 14.2829 11.7741 13.8961V3.6873M3.75293 3.6873V2.22891C3.75293 1.84211 3.90658 1.47117 4.18008 1.19766C4.45359 0.92416 4.82454 0.770508 5.21133 0.770508H8.12812C8.51492 0.770508 8.88586 0.92416 9.15937 1.19766C9.43287 1.47117 9.58652 1.84211 9.58652 2.22891V3.6873M5.21133 7.3333V11.7085M8.12812 7.3333V11.7085"
      stroke="currentColor"
    />
  </svg>
)

const labelsIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M3 2v4.586l7 7L14.586 9l-7-7zM2 2a1 1 0 0 1 1-1h4.586a1 1 0 0 1 .707.293l7 7a1 1 0 0 1 0 1.414l-4.586 4.586a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 2 6.586z"/>
    <path d="M5.5 5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1m0 1a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M1 7.086a1 1 0 0 0 .293.707L8.75 15.25l-.043.043a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 0 7.586V3a1 1 0 0 1 1-1z"/>
  </svg>
)

export default CVItem
